import React, {useCallback, useEffect, useRef, useState} from "react";
import {createUseStyles} from "react-jss";
import {Section} from "../Section/Section";
import {AppBar} from "../AppBar/AppBar";
import {useProgressState} from "../progress/progressContext";
import {ChapterProgress} from "../progress/ChapterProgress";
import {useFontsUsed} from "../fonts/useFontsUsed";
import {ScrollPromptButton} from "./ScrollPromptButton";
import {useAudioAutoplay} from "../AudioGuide/useAudioAutoplay";
import {AudioPlayerBar} from "../AudioGuide/AudioPlayerBar";
import {findActiveSectionIndex} from "../Admin/sidebarHelpers";
import {OverlayScrollbar} from "./OverlayScrollbar";

const useStyles = createUseStyles({
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        maxHeight: "100dvh",
        position: "relative",
        zIndex: 0
    },
    content: {
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-y",
        scrollBehavior: "smooth",
        backgroundColor: "#ffffff",
        height: "calc(100% - 64px)",
        transition: "margin-left 150ms ease-in",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
            display: "none",
            width: 0,
            height: 0,
        },
    },
    story: {
        position: ({isSticky}) => (isSticky ? "sticky" : "relative"),
        zIndex: 10,
    },
    "@global": {
        body: {margin: 0},
    },
});

export const Story = ({
                          data = {},
                          isAdminMode = false,
                          onSectionChange = () => {
                          },
                          onClose,
                          showSidePanel,
                      }) => {
    const safeData = data || {};
    const {
        isSticky = false,
        color = "white",
        sections = [],
        appBar = {},
        about = {},
        audioGuide = {},
        autoplay = {},
    } = safeData;

    const classes = useStyles({
        color: color,
        isSticky: isSticky,
        isAdminMode: isAdminMode,
        showSidePanel: showSidePanel,
        mode: appBar?.mode,
    });
    const [currentSection, setCurrentSection] = useState(0);
    const scrollContainerRef = useRef(null);
    const sortedSections = [...(sections || [])].sort((a, b) => (a?.order || 0) - (b?.order || 0));
    const {locks = []} = useProgressState() || { locks: [] };
    useFontsUsed({loadAll: isAdminMode});

    const findUnlockedSections = () => {
        const sectionsWithLock = (sections || []).map((section, sectionId) => ({
            ...section,
            id: sectionId,
            locked: Boolean((locks || []).find(lock => lock?.sectionId === sectionId))
        }));

        const firstLockedSection = sectionsWithLock.find(section => section.locked);
        return isAdminMode
            ? sortedSections
            : sortedSections.slice(0, firstLockedSection?.id != null ? firstLockedSection.id + 1 : sortedSections.length);
    };

    const exhibition = () => {
        if (about?.exhibition) {
            return about.exhibition;
        } else {
            return appBar?.exhibition;
        }
    };

    const museum = () => {
        if (about?.museum) {
            return about.museum;
        } else {
            return appBar?.museum;
        }
    };

    const handleAutoplaySectionChange = useCallback((sectionId) => {
        setCurrentSection(sectionId);
        if (onSectionChange) onSectionChange(sectionId);
    }, [onSectionChange]);

    const { state: audioState, handlers: audioHandlers } = useAudioAutoplay({
        story: safeData,
        currentSectionIndex: currentSection ?? 0,
        onSectionChange: handleAutoplaySectionChange,
    });

    const showAudioPlayer = !isAdminMode && Boolean(
        audioGuide?.enabled === true ||
        safeData?.audioGuide?.enabled === true ||
        autoplay?.enabled === true ||
        safeData?.autoplay?.enabled === true ||
        (sections || []).some((s) => Boolean(s?.audioTrack?.url))
    );

    useEffect(() => {
        onSectionChange(currentSection);
    }, [currentSection, onSectionChange]);

    const handleSectionViewChange = useCallback((index) => {
        const container = scrollContainerRef.current;
        if (container && container.clientHeight > 0) {
            return;
        }
        if (!audioState.isAutoplayActive && !audioState.isPlaying) {
            setCurrentSection(index);
        }
    }, [audioState.isAutoplayActive, audioState.isPlaying]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const updateActiveSectionFromScroll = () => {
            if (audioState.isAutoplayActive || audioState.isPlaying) return;
            const containerRect = container.getBoundingClientRect();
            if (containerRect.height === 0 && container.clientHeight === 0) return;

            const sectionEls = container.querySelectorAll("main > section");
            if (!sectionEls || sectionEls.length === 0) return;
            const rects = Array.from(sectionEls).map((el) => el.getBoundingClientRect());
            const activeIdx = findActiveSectionIndex(
                rects,
                containerRect.top,
                containerRect.height,
                container.scrollTop,
                container.scrollHeight
            );
            setCurrentSection(activeIdx);
        };

        updateActiveSectionFromScroll();
        container.addEventListener("scroll", updateActiveSectionFromScroll, { passive: true });
        window.addEventListener("resize", updateActiveSectionFromScroll);
        return () => {
            container.removeEventListener("scroll", updateActiveSectionFromScroll);
            window.removeEventListener("resize", updateActiveSectionFromScroll);
        };
    }, [sortedSections.length, audioState.isAutoplayActive, audioState.isPlaying]);

    return (
        <div className={classes.contentContainer}>
            <AppBar
                onClose={onClose}
                exhibition={exhibition()}
                museum={museum()}
                mode={appBar?.mode}
                backgroundColor={appBar?.backgroundColor}
                isAdminMode={isAdminMode}
            />
            <div className={classes.content} id="scrollytell-content" ref={scrollContainerRef}>
                <ChapterProgress
                    show={!isAdminMode}
                />
                <main className={classes.story}>
                    {findUnlockedSections()?.map((section, i) => (
                        <Section
                            key={`section-${i}`}
                            sectionId={i}
                            isAdminMode={isAdminMode}
                            section={section}
                            onChange={handleSectionViewChange}
                        />
                    ))}
                </main>
            </div>
            {!isAdminMode && <ScrollPromptButton targetId="scrollytell-content" />}
            <OverlayScrollbar containerRef={scrollContainerRef} />
            {showAudioPlayer && (
                <AudioPlayerBar state={audioState} handlers={audioHandlers} />
            )}
        </div>
    );
};
